import { skipToken } from "@reduxjs/toolkit/query";
import type { CopilotProposalSummary } from "@repo/app-types/entities/copilotProposal";
import {
  buildGradingOutcome,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  PlaybookBranch,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { TeammateAgentTestCaseProposalData } from "@repo/app-types/entities/teammateAgentProposal";
import { useState, type FC } from "react";
import { useNavigate } from "react-router";
import { useResolveProposalMutation } from "../../api/entities/copilotProposal.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import {
  useCreatePlaybookTestSpecMutation,
  useDeletePlaybookTestSpecMutation,
  useUpdatePlaybookTestSpecMutation,
} from "../../api/entities/playbookTestSpec.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { useAppDispatch } from "../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { CopilotProposalCard } from "../researchAgent/copilotProposalCard.js";
import { readTeammateAgentProposalCardContent } from "./teammateAgentProposalCardContent.js";

export interface TeammateAgentTestCaseProposalCardProps {
  proposal: CopilotProposalSummary<"teammateAgent">;
  data: TeammateAgentTestCaseProposalData;
}

/**
 * A voice agent test case the copilot proposes to create, change or remove.
 * Test specs have no draft to review in, so the card's action (Create, Update
 * or Remove test case) applies the proposal:
 * it writes the spec (a new one takes the id the proposal minted), records
 * the proposal as applied, and opens the Testing tab on the case. A write
 * that fails leaves the card pending.
 */
export const TeammateAgentTestCaseProposalCard: FC<
  TeammateAgentTestCaseProposalCardProps
> = (props) => {
  const { proposal, data } = props;
  const teammateAgentId = proposal.resource.id;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);
  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;
  const { data: playbook } = usePlaybookByIdQuery(
    playbookId ? { id: playbookId, branch: PlaybookBranch.Draft } : skipToken,
  );

  const [createTestSpec] = useCreatePlaybookTestSpecMutation();
  const [updateTestSpec] = useUpdatePlaybookTestSpecMutation();
  const [deleteTestSpec] = useDeletePlaybookTestSpecMutation();
  const [resolveProposal] = useResolveProposalMutation();
  const [isApplying, setIsApplying] = useState(false);

  const notifyFailure = (primaryMessage: string) => {
    showNotification(dispatch, {
      id: `test-case-proposal-${proposal.id}-${Date.now().toString()}`,
      type: NotificationType.FAILURE,
      primaryMessage,
    });
  };

  const apply = async (
    playbookId: PlaybookId,
    outcomeDefinition: OutcomeDefinition | undefined,
  ) => {
    setIsApplying(true);
    try {
      const buildExpected = (expectedOutcome: string | null) => ({
        nodes: {},
        outcomes:
          outcomeDefinition && expectedOutcome
            ? buildGradingOutcome(outcomeDefinition, expectedOutcome)
            : undefined,
      });
      switch (data.kind) {
        case "create_test_case":
          await createTestSpec({
            playbookId,
            payload: {
              proposal_id: proposal.id,
              name: data.name,
              description: data.description,
              expected: buildExpected(data.expected_outcome),
            },
          }).unwrap();
          break;
        case "update_test_case":
          await updateTestSpec({
            playbookId,
            testSpecId: data.test_spec_id,
            payload: {
              name: data.proposed.name,
              description: data.proposed.description,
              expected: buildExpected(data.proposed.expected_outcome),
            },
          }).unwrap();
          break;
        case "delete_test_case":
          await deleteTestSpec({
            playbookId,
            testSpecId: data.test_spec_id,
          }).unwrap();
          break;
      }
    } catch (err) {
      notifyFailure("Failed to apply the test case. Please try again.");
      setIsApplying(false);
      return;
    }
    // The test spec is written, so the proposal is applied. One that has
    // moved on (409) was already decided; the write stands either way.
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "applied",
      }).unwrap();
    } catch (err) {
      if (isConflictQueryError(err)) {
        notifyFailure("This suggestion is no longer available.");
      } else {
        console.error("Failed to mark the proposal as applied:", err);
        notifyFailure(
          "The test case was saved, but the suggestion could not be marked as applied.",
        );
      }
    } finally {
      setIsApplying(false);
    }
    void navigate(
      readTeammateAgentProposalCardContent(proposal).resourceLocation,
    );
  };

  return (
    <CopilotProposalCard
      proposal={proposal}
      // The action waits for the draft playbook: the expected outcome is written
      // against its outcome definition.
      onReview={
        playbookId && playbook
          ? () => {
              void apply(
                playbookId,
                getOutcomeDefinitionFromPlaybook(playbook),
              );
            }
          : undefined
      }
      isSaving={isApplying}
    />
  );
};
